import { isNothing } from "./maybe";
/**
 * Scroll a vertical overflow host just enough to keep a target rect visible.
 * Used by the storyboard (growing synopsis fields, newly created cards) where
 * the board owns scroll — not the window.
 */

import { computeScrollDelta } from "../editor/scrollIntoView";

export type DomScrollOptions = {
  /** Extra inset from the scroller's visible edges. Defaults to 24. */
  margin?: number;
};

/**
 * Adjust `scroller.scrollTop` so `[top, bottom]` (viewport coords) sits inside
 * the scroller's visible box, with `margin` of padding from each edge.
 *
 * Prefer "nearest": only scroll when the target is clipped. Do not recenter
 * on every keystroke (that fights the user when they deliberately scrolled).
 */
export function ensureRectInScroller(
  scroller: HTMLElement,
  target: { top: number; bottom: number },
  opts: DomScrollOptions = {},
): void {
  const margin = opts.margin ?? 24;
  const delta = computeScrollDelta(target, scroller.getBoundingClientRect(), {
    y: "nearest",
    yMargin: margin,
  });
  if (isNothing(delta)) return;
  scroller.scrollTop += delta.value;
}

/** Keep the current text selection / caret inside `scroller`. */
export function ensureSelectionInScroller(
  scroller: HTMLElement,
  opts?: DomScrollOptions,
): void {
  const sel = document.getSelection();
  if (sel === null || sel.rangeCount === 0) return;
  const range = sel.getRangeAt(0);
  const rect = range.getBoundingClientRect();
  // A collapsed caret sometimes reports a zero-size rect; fall back to the
  // active field's box so growing contenteditable synopsis fields still pin.
  if (rect.height === 0 && rect.width === 0) {
    const active = document.activeElement;
    if (
      active instanceof HTMLElement === false ||
      scroller.contains(active) === false
    ) {
      return;
    }
    const box = active.getBoundingClientRect();
    ensureRectInScroller(
      scroller,
      { top: box.bottom - 1, bottom: box.bottom },
      opts,
    );
    return;
  }
  if (scroller.contains(range.commonAncestorContainer) === false) return;
  ensureRectInScroller(scroller, rect, opts);
}

/** Keep an element's box inside `scroller` (new cards, act headers, etc.). */
export function ensureElementInScroller(
  scroller: HTMLElement,
  el: Element,
  opts?: DomScrollOptions,
): void {
  const rect = el.getBoundingClientRect();
  ensureRectInScroller(scroller, rect, opts);
}
